import { CalendarDays, CircleUser, House, UserRound, Users, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import NotificationBell from '../components/common/NotificationBell';
import logoPsi from '../assets/logoPsiMammoliti.svg';
import { PsiChatWidget } from '../components/psychologist/home/PsiChatWidget';
import { useNzJourneyStatus } from '../hooks/useNzJourneyStatus';
import CircularProgress from '../components/common/CircularProgress';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../components/ui/dialog';
import { AppShellContext } from '../contexts/AppShellContext';
import { PwaPushGate } from './PwaPushGate';
import { useLoadPsychologist } from './useLoadPsychologist';
import { usePushHeartbeat } from './usePushHeartbeat';
import { usePwaRestriction } from './usePwaRestriction';

/** Alto de la barra inferior, para que el contenido no quede tapado (se suma el área segura del iPhone). */
export const PWA_NAV_HEIGHT = '4rem';
/** Alto de la barra superior (con la campanita). La campanita despliega su panel justo debajo: `top-16`. */
export const PWA_HEADER_HEIGHT = '4rem';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end: boolean;
  /** Se oculta (y la ruta no se puede abrir) mientras la psicóloga está restringida a la Agenda — ver `usePwaRestriction`. */
  restricted?: boolean;
  /** Solo para quienes tienen acceso a Home (`useNzJourneyStatus.canAccessHome`), igual que la pestaña de la web. */
  requiresHome?: boolean;
}

/** Barra inferior: Home | Agenda | Pacientes | Comunidad. Perfil no está acá: se abre desde la barra de arriba. */
const NAV_ITEMS: NavItem[] = [
  { to: '/home', label: 'Home', icon: House, end: false, requiresHome: true },
  { to: '/calendar', label: 'Agenda', icon: CalendarDays, end: false },
  { to: '/patients', label: 'Pacientes', icon: UserRound, end: false, restricted: true },
  { to: '/comunidad', label: 'Comunidad', icon: Users, end: false },
];

/** Perfil (con Notificaciones adentro) se abre desde un ícono en la barra de arriba. */
const PROFILE_PATH = '/profile';

/**
 * Marco de la app mobile con sesión. Va detrás del bloqueo por notificaciones (`PwaPushGate`): hasta que estén activadas
 * solo se ve la pantalla de activación. Ya activadas, una barra superior con la campanita, el contenido de cada pantalla y
 * una barra inferior con Home, Agenda, Pacientes y Comunidad (Home solo con acceso, como en la web) y, arriba, el acceso a Perfil
 * (Notificaciones es una sección de Perfil). Es todo lo que la app mobile expone del resto de la plataforma; sumar una sección
 * nueva es sumar un ítem acá y una ruta en `PwaApp`.
 *
 * Hace por Perfil lo que `AppShell` hace en la web: provee `AppShellContext` y, si Perfil tiene cambios sin guardar, pide
 * confirmación antes de cambiar de sección con la barra.
 */
export function PwaShell() {
  return (
    <PwaPushGate>
      <PwaShellFrame />
    </PwaPushGate>
  );
}

function PwaShellFrame() {
  // El fondo lo pone el marco (no cada pantalla): las pantallas de Comunidad son `min-h-full` y, sin un alto
  // definido arriba, solo pintan hasta donde llega su contenido — debajo se vería blanco.
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inComunidad = pathname.startsWith('/comunidad');
  const inHome = pathname.startsWith('/home');
  useLoadPsychologist();
  usePushHeartbeat();
  // Mismo gate que en la web: mientras está en el journey NZ no se le muestra el bot (tiene el JourneyBot).
  const { canAccessHome, loading: nzJourneyLoading } = useNzJourneyStatus();
  const restriction = usePwaRestriction();

  // Home solo para quienes tienen acceso (la web lo decide igual en `AppShell`); mientras se averigua no se muestra la pestaña.
  const homeVisible = !nzJourneyLoading && canAccessHome;
  // NZ recién creada: solo la Agenda hasta que cargue horarios (en la web). Pacientes y Perfil no se abren ni por URL.
  const navItems = NAV_ITEMS.filter(
    (item) => (!item.restricted || !restriction.restricted) && (!item.requiresHome || homeVisible),
  );
  const blockedRoute =
    restriction.restricted &&
    (pathname.startsWith(PROFILE_PATH) || NAV_ITEMS.some((i) => i.restricted && pathname.startsWith(i.to)));
  // Sin acceso a Home, esa ruta lleva a la Agenda (como `AppShell` en la web).
  const homeBlocked = inHome && !nzJourneyLoading && !canAccessHome;

  // Cambios sin guardar en Perfil o en la configuración de la Agenda (horarios, día específico, bloqueo): los avisan
  // `PsychologistProfile` y `PwaAgenda` vía `AppShellContext`, igual que en la web.
  const profileDirtyRef = useRef(false);
  const calendarDirtyRef = useRef(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const [pendingWhat, setPendingWhat] = useState<'perfil' | 'agenda'>('perfil');
  const isDirty = () => profileDirtyRef.current || calendarDirtyRef.current;
  const askToLeave = (to: string) => {
    setPendingWhat(calendarDirtyRef.current ? 'agenda' : 'perfil');
    setPendingPath(to);
  };
  const shellContext = useMemo(
    () => ({
      setProfileDirty: (dirty: boolean) => {
        profileDirtyRef.current = dirty;
      },
      setCalendarDirty: (dirty: boolean) => {
        calendarDirtyRef.current = dirty;
      },
      // La Agenda de la app no tiene tours.
      setCalendarTourRunning: () => undefined,
    }),
    [],
  );
  // Salir de Perfil por otro camino (la campanita, un link) descarta el aviso: no queda un "sin guardar" fantasma.
  useEffect(() => {
    if (!pathname.startsWith('/profile')) profileDirtyRef.current = false;
    if (!pathname.startsWith('/calendar')) calendarDirtyRef.current = false;
  }, [pathname]);
  const guardNavigation = useCallback(
    (event: React.MouseEvent, to: string) => {
      const alreadyThere = pathname.startsWith(to);
      if ((profileDirtyRef.current || calendarDirtyRef.current) && !alreadyThere) {
        event.preventDefault();
        askToLeave(to);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname],
  );

  // Los links del bot respetan el aviso de cambios sin guardar (Perfil o Agenda), igual que la barra de abajo.
  const navigateFromChat = useCallback(
    (to: string) => {
      if (isDirty()) askToLeave(to);
      else navigate(to);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate],
  );

  if (blockedRoute) return <Navigate to="/" replace />;
  if (homeBlocked) return <Navigate to="/calendar" replace />;

  return (
    <div
      className={`flex min-h-[100dvh] flex-col ${inComunidad ? 'bg-[#E6DBFA]' : inHome ? 'bg-[#423568]' : 'bg-[#F3EFFF]'}`}
      // Dónde tienen que pegarse los encabezados "sticky" de las pantallas (los días de la semanal): justo DEBAJO de la barra de
      // arriba, no contra el borde de la pantalla.
      style={{ ['--psi-sticky-top' as string]: `calc(${PWA_HEADER_HEIGHT} + env(safe-area-inset-top))` }}
    >
      {/* Barra superior: la misma de la web (violeta, marca + campanita). La campanita es blanca sobre violeta: no se vería
          sobre un fondo claro. */}
      <header
        className="admin-header-modern sticky top-0 z-40 px-4 pt-[env(safe-area-inset-top)]"
        style={{ height: `calc(${PWA_HEADER_HEIGHT} + env(safe-area-inset-top))` }}
      >
        <div className="flex items-center gap-2.5">
          <img src={logoPsi} alt="" aria-hidden width={32} height={32} />
          <span className="text-base font-bold text-white">Psi Platform</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell />
          {/* Perfil (con Notificaciones adentro): arriba, junto a la campanita. Oculto mientras está restringida a la Agenda. */}
          {!restriction.restricted && (
            <NavLink
              to={PROFILE_PATH}
              aria-label="Perfil"
              onClick={(event) => guardNavigation(event, PROFILE_PATH)}
              className={({ isActive }) =>
                `grid h-10 w-10 place-items-center rounded-full text-white no-underline transition-colors ${
                  isActive ? 'bg-white/25' : 'hover:bg-white/10'
                }`
              }
            >
              <CircleUser size={24} aria-hidden />
            </NavLink>
          )}
        </div>
      </header>

      <div
        className="flex-1"
        style={{ paddingBottom: `calc(${PWA_NAV_HEIGHT} + env(safe-area-inset-bottom))` }}
      >
        {restriction.restricted && (
          <div role="status" className="mx-4 mt-4 rounded-2xl bg-white p-4 text-sm text-[#2d2049] shadow-sm">
            <strong className="block text-base">Para empezar, configurá tus horarios</strong>
            Cargá tus horarios de trabajo desde la plataforma web (en una computadora). Mientras tanto, desde la app podés ver tu
            Agenda.
          </div>
        )}
        <AppShellContext.Provider value={shellContext}>
          {inHome && nzJourneyLoading ? (
            // Todavía no se sabe si tiene acceso a Home: sin esto se vería un instante y después saltaría a la Agenda.
            <div className="grid place-items-center py-20">
              <CircularProgress />
            </div>
          ) : (
            <Outlet />
          )}
        </AppShellContext.Provider>
      </div>

      <nav
        aria-label="Secciones"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-[#E5E0FF] bg-white pb-[env(safe-area-inset-bottom)]"
      >
        {navItems.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={(event) => guardNavigation(event, to)}
            className={({ isActive }) =>
              `flex h-16 flex-1 flex-col items-center justify-center gap-1 text-xs font-semibold no-underline ${
                isActive ? 'text-[#6D57BC]' : 'text-[#8b86a0]'
              }`
            }
          >
            <Icon size={20} aria-hidden />
            {label}
          </NavLink>
        ))}
      </nav>

      {/* Detrás de `PwaPushGate`: en la pantalla de bloqueo por notificaciones no se monta. */}
      {!nzJourneyLoading && canAccessHome && <PsiChatWidget onNavigate={navigateFromChat} />}

      <Dialog open={pendingPath !== null} onOpenChange={(open) => !open && setPendingPath(null)}>
        <DialogContent className="max-w-sm p-6">
          <DialogTitle>Cambios sin guardar</DialogTitle>
          <DialogDescription>
            {pendingWhat === 'agenda'
              ? 'Tenés cambios sin guardar en la configuración de tu agenda. Si salís ahora, se perderán.'
              : 'Tenés cambios sin guardar en tu perfil. Si salís ahora, se perderán.'}
          </DialogDescription>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPendingPath(null)}>
              Seguir editando
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                const target = pendingPath;
                profileDirtyRef.current = false;
                calendarDirtyRef.current = false;
                setPendingPath(null);
                if (target) navigate(target);
              }}
            >
              Salir sin guardar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
